import * as React from "react";
import type { VariantProps } from "class-variance-authority";

import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button/buttonVariants";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePermissions, type PermissionAction } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";

type ButtonBaseProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
  };

interface PermissionButtonProps extends ButtonBaseProps {
  section: PLATFORM_SECTION_ENUM;
  action: PermissionAction;
  mode?: "disable" | "hide";
  permissionTooltip?: string;
}

export const PermissionButton = React.forwardRef<
  HTMLButtonElement,
  PermissionButtonProps
>(
  (
    {
      section,
      action,
      mode = "disable",
      permissionTooltip = "You don't have permission to perform this action",
      ...buttonProps
    },
    ref,
  ) => {
    const { can, isLoading } = usePermissions();

    // Fail CLOSED while permissions are in flight — never render an enabled
    // gated action before we know the user is allowed (mirrors PermissionGuard).
    // `hide` renders nothing; `disable` renders a disabled button (no tooltip,
    // since "loading" isn't a permission denial).
    if (isLoading) {
      return mode === "hide" ? null : (
        <Button ref={ref} {...buttonProps} disabled />
      );
    }

    const allowed = can(section, action);

    if (!allowed && mode === "hide") return null;

    if (!allowed) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button ref={ref} {...buttonProps} disabled />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{permissionTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <Button ref={ref} {...buttonProps} />;
  },
);

PermissionButton.displayName = "PermissionButton";
